import { useIsEmbeddedBridgeOnly } from '#~/agent/hooks.js';
import { useAnalytics } from '#~/analytics/index.js';
import { Icon } from '@studio/design-system/icon';
import { Spinner } from '@studio/design-system/spinner';
import {
  verificationMessage,
  verificationSeverity,
  verificationTitle
} from '../connection-verification.js';
import { Callout } from '@studio/design-system/callout';
// Domain analytics tracker (merged from main): the settings-lifecycle
// tracker replaces the old `track(EVENT_NAMES.…)` calls for advanced-
// settings section opens. See knowledge: analytics domain trackers.
import { createSettingsLifecycleTracker } from '#~/analytics/domains/index.js';
import type { HtmlAttributes } from '@studio/design-system/props';
import * as Tabs from '@studio/design-system/tabs';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { Arrow, Tooltip } from '@studio/design-system/tooltip';
import type { Agent } from '@studio/models/agent';
import type { Device, Model } from '@studio/models/device';
import { DefaultModelId } from '@studio/models/device';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useMultiDeviceStateQuery } from '#~/statequery/hook.js';
import { Sel, STag } from '#~/stag/index.js';
import { AuthenticationStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { MultiDeviceSelector } from '#~/statequery/types.js';
import { twMerge } from 'cn';
import { Columns, HRule } from '../layout.jsx';
import { SectionDescription } from '../layout/section-description.js';
import { useProducer, useStore } from '../store/hooks.js';
// Transitory-placeholder slice (W2, spec §4.1): when the candidate is an
// unauthenticated placeholder, only the Authentication tab is shown.
import { useTransitoryDevice } from '#~/device/transitory.js';
import { useTemplate } from '#~/device/registry/hooks.js';
import { isUnlistedBrandDriver } from '#~/device/setup-attention.js';
import { Preview } from '../preview.jsx';
import { SUBCATEGORY_TRIGGER_CLASSNAME } from '../types.js';
import { AuthenticationContent } from './authentication-content.js';
import { filterAvailableTabs, isTabLocked } from './available-tabs.js';
import { ControlContent } from './control-content.js';
import { consumeDefaultConnectionTab } from './default-tab-signal.js';
import { DriverContent } from './driver-content.js';
import { MonitoringContent } from './monitoring-content.js';
import { useAuthenticationPending } from './use-authentication-pending.js';
import { useNeedAttentionTabs } from './use-need-attention-tabs.js';
import { VideoContent } from './video-content.js';

/** Credential plane the camera rejected; video wins because it also blocks the picture. */
type RejectedCredentials = 'video' | 'control';

const rejectedCredentialsSelector: MultiDeviceSelector<T_Maybe<RejectedCredentials>> = {
  minInterval: 200,
  transform: (device) =>
    device.status?.video?.authenticationStatus ===
    AuthenticationStatus.AUTHENTICATION_STATUS_INVALID
      ? 'video'
      : device.status?.movement?.authenticationStatus ===
          AuthenticationStatus.AUTHENTICATION_STATUS_INVALID
        ? 'control'
        : undefined
};

/**
 * Secondary tab options for Iris Connection.
 *
 * The `'authentication'` tab is the deep-link target for the per-tile
 * chip's "Authenticate" verb. Exported so the deep-link prop chain
 * (Settings.defaultTab) can be typed end-to-end.
 */
export type IrisConnectionTab = 'monitoring' | 'driver' | 'video' | 'control' | 'authentication';

// Each warning explains the observed problem and the next action in that panel.
const TAB_WARNINGS: Record<
  Exclude<IrisConnectionTab, 'monitoring'>,
  { title: string; message: string }
> = {
  authentication: {
    title: 'Camera credentials rejected',
    message:
      'The camera did not accept its saved credentials. Enter the camera’s username and password below, then select Submit.'
  },
  video: {
    title: 'Video connection failed',
    message:
      'The camera’s video stream could not be reached. Check the video address, port, and stream path below, then select Save Changes. If Authentication also has a warning, update the credentials there.'
  },
  control: {
    title: 'Camera control connection failed',
    message:
      'The camera’s controls could not be reached. Check the camera brand and model in Driver, then check the control connection type, address, and port below and select Save Changes. If Authentication also has a warning, update the credentials there.'
  },
  driver: {
    title: 'Generic camera driver',
    message:
      'This camera is using a generic driver. Only basic commands are supported. Choose the camera’s brand and model below, then select Save Changes to use its supported controls.'
  }
};

// A chosen "Other – brand is not listed" driver is finished: explain its limits, ask for nothing.
const UNLISTED_BRAND_NOTE = {
  title: 'Generic camera driver',
  message:
    'This camera uses a generic driver because its brand is not listed. Only basic commands are supported.'
};

/** Why a locked tab refuses activation; shown as its tooltip and screen-reader description. */
const LOCKED_TAB_REASON = 'Complete authentication first';

const CREDENTIALS_REQUIRED_WARNING = {
  title: 'Camera credentials required',
  message: 'Enter the camera’s username and password below, then select Submit.'
};

// A control-only prompt over a working picture reads as an error unless it names what it is for.
const CONTROL_CREDENTIAL_WARNINGS = {
  required: {
    title: 'Camera control credentials required',
    message:
      'The camera requires credentials for control commands such as PTZ and camera settings. Video is not affected. Enter the camera’s username and password below, then select Submit.'
  },
  rejected: {
    title: 'Camera control credentials rejected',
    message:
      'The camera did not accept its saved credentials for control commands such as PTZ and camera settings. Video is not affected. Enter the camera’s username and password below, then select Submit.'
  }
};

/** Authentication copy names the rejecting plane and never calls an unsaved password rejected. */
const authenticationWarning = (
  rejected: T_Maybe<RejectedCredentials>,
  hasSavedCredentials: boolean
): { title: string; message: string } => {
  if (rejected === 'control') {
    return CONTROL_CREDENTIAL_WARNINGS[hasSavedCredentials ? 'rejected' : 'required'];
  }
  return hasSavedCredentials ? TAB_WARNINGS.authentication : CREDENTIALS_REQUIRED_WARNING;
};

/** Props for IrisConnectionSection component */
interface IrisConnectionSectionProps extends HtmlAttributes {
  agent?: Agent;

  /**
   * Initial active tab. When provided, `IrisConnectionSection` opens
   * with this tab selected instead of the default `'monitoring'`.
   * Used by the deep-link path: chip click → Settings.defaultTab →
   * here.
   */
  defaultTab?: IrisConnectionTab;

  /**
   * Optional device identity + state used to derive the per-sub-tab
   * "needs attention" warning icon (Multi-Add Phase 2). When
   * omitted, no icon ever renders — older call sites continue to work
   * without modification. Scaffold wires this from the settings
   * store's `candidate` + `agent.status`.
   */
  device?: {
    id: Device['id'];
    modelId?: T_Maybe<Model['id']>;
    agentStatus?: T_Maybe<Agent['status']>;
  };
}

/**
 * Secondary tab configuration.
 *
 * Control sits between Video and Driver, matching the Command Universe taxonomy
 * §8 tree, which declares an `Iris Connection → Control` subsection alongside
 * the top-level Control tab. Both placements are intentional and carry
 * different content: this sub-tab owns the platform control protocol (and the
 * PTZ pad), the top-level tab the camera's registry Controls. See
 * control-content.tsx.
 */
const SECONDARY_TABS: Array<{ value: IrisConnectionTab; label: string }> = [
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'authentication', label: 'Authentication' },
  { value: 'video', label: 'Video' },
  { value: 'control', label: 'Control' },
  { value: 'driver', label: 'Driver' }
];

/** Tab descriptions mapping */
const TAB_DESCRIPTIONS: Record<IrisConnectionTab, { title: string; description: string }> = {
  monitoring: {
    title: 'Connection Monitoring',
    description: 'Monitor the real-time status and health of your Iris camera connection.'
  },
  authentication: {
    title: 'Authentication',
    description: 'Manage device credentials and authentication.'
  },
  video: {
    title: 'Video Settings',
    description: 'Connection that brings video from your device into the Studio'
  },
  // Taxonomy §9 wording for the subsection: it scopes this sub-tab to the
  // control protocol Iris itself speaks, which is the part of control that
  // belongs to the connection rather than to the camera's own feature set.
  control: {
    title: 'Control Configuration',
    description: 'Configuration of the control protocol used by Iris to command your device'
  },
  driver: {
    title: 'Driver',
    description: 'Loads the compatible commands for your device'
  }
};

/**
 * IrisConnectionSection component with secondary tab navigation
 *
 * This component provides a structured interface for managing Iris camera connections
 * with dedicated sections for monitoring, video, control, and driver settings.
 * Control is deliberately reachable from two places — here (the Iris control
 * protocol form and PTZ pad, hosted nowhere else) and the top-level Control tab
 * (the camera's own registry Controls, and only those) — per the Command
 * Universe taxonomy §8/§9.
 */
export const IrisConnectionSection = ({
  agent,
  className,
  defaultTab,
  device,
  ...props
}: IrisConnectionSectionProps): ReactElement => {
  const candidateId = useStore((µ) => µ.candidate.id);
  const settingsRoomId = useStore((µ) => µ.roomId);
  // Same verdict as the "Authenticate" badge that deep-links here; locks every other tab.
  const authenticationPending = useAuthenticationPending(
    candidateId,
    settingsRoomId ?? agent?.roomId
  );
  const lockReasonId = useId();

  // Check for a deep-link signal (set by ConfigureBadge before opening
  // settings). When the parent passes `defaultTab` (deep-link path from
  // the per-tile chip), open with that tab pre-selected; otherwise
  // default to 'monitoring' (the original behavior).
  const signalTab = consumeDefaultConnectionTab();
  const [activeTab, setActiveTab] = useState<IrisConnectionTab>(() => {
    const initialTab = signalTab ?? defaultTab ?? 'monitoring';
    // A camera waiting for credentials opens on Authentication, never on a locked tab.
    return isTabLocked(initialTab, authenticationPending) ? 'authentication' : initialTab;
  });
  const [isLoading, setIsLoading] = useState(false);
  const { track } = useAnalytics();
  // Domain analytics tracker (merged from main): owns the advanced-
  // settings section-open event taxonomy. Replaces HEAD's old inline
  // `track(EVENT_NAMES.EDIT_ADVANCED_SETTINGS, …)` analytics props,
  // which are now dead and were dropped from this component's interface.
  const settingsLifecycleTracker = useMemo(() => createSettingsLifecycleTracker(track), [track]);

  // Mirror the active sub-tab to the settings store so the footer's
  // global Save handler can read the user's current sub-tab at commit
  // time. Local `activeTab` is still the source of truth for THIS
  // component's render; the store mirror is only consumed by external
  // readers (the footer / wiring).
  const settingsProducer = useProducer();

  // Derive the per-sub-tab needs-attention booleans for this device.
  // Returns all-false when `device` is undefined (old call sites pass
  // nothing → no icon ever shows).
  // Per `hook-vs-component-integration-tests`
  // (ai.knowledge/platform/studio.client): the hook is the seam tests
  // exercise via `renderHook`. Component-level test only verifies the
  // render contract (icon renders for the right tab when boolean
  // true).
  const needsAttentionTabs = useNeedAttentionTabs(device);
  const verification = useStore((state) => state.verification);
  // Read saved configuration: typing a draft is not evidence of rejected credentials.
  const hasSavedCredentials = useStore((state) => {
    const original = state.lifecycle?.original;
    return [original?.video?.config, original?.control?.config].some(
      (config) =>
        config &&
        'credentials' in config &&
        !!(config.credentials?.username || config.credentials?.password)
    );
  });
  const driverTemplate = useTemplate(device?.modelId);
  const studioEmbeddedOnly = useIsEmbeddedBridgeOnly();
  const isEmbeddedContext = agent?.embedded ?? studioEmbeddedOnly;
  // Is the candidate an unauthenticated transitory placeholder (spec
  // §4.1)? Look the candidate id up in the transitory slice. When it is,
  // only the Authentication tab is shown — there is no real device to
  // configure yet.
  const candidateModelId = useStore((µ) => µ.candidate.modelId);
  const isTransitory = !!useTransitoryDevice(candidateId);
  // The Authentication tab is ALWAYS visible (URX-only flow shows the
  // form; Iris-enabled flow shows the disabled informational state).
  // Existing video/control/driver tabs remain hidden in embedded
  // contexts as before; a transitory placeholder shows ONLY
  // Authentication. Filter logic lives in `filterAvailableTabs` (pure
  // helper, unit-tested).
  const availableTabs = filterAvailableTabs(SECONDARY_TABS, isEmbeddedContext, isTransitory);

  const pendingAuthContinuation = useStore((µ) => µ.flags.pendingAuthContinuation);
  const rejectedCredentials = useMultiDeviceStateQuery(rejectedCredentialsSelector)[candidateId];
  const authInvalid = !!rejectedCredentials;
  const warning =
    activeTab !== 'monitoring' && needsAttentionTabs[activeTab]
      ? activeTab === 'authentication'
        ? authenticationWarning(rejectedCredentials, hasSavedCredentials)
        : TAB_WARNINGS[activeTab]
      : undefined;
  // Same template lookup as useNeedAttentionTabs, so the note and the driver warning never overlap.
  const unlistedBrandNote =
    activeTab === 'driver' &&
    !warning &&
    isUnlistedBrandDriver(device?.modelId, driverTemplate?.model)
      ? UNLISTED_BRAND_NOTE
      : undefined;

  useEffect(() => {
    if (!pendingAuthContinuation || (!authInvalid && verification?.status !== 'auth-required')) {
      return;
    }

    settingsProducer((draft) => {
      draft.flags.pendingAuthContinuation = false;
    });

    if (activeTab !== 'authentication') {
      setActiveTab('authentication');
    }
  }, [pendingAuthContinuation, authInvalid, verification?.status, activeTab, settingsProducer]);

  const handleCredentialsSaved = useLatestFn(() => {
    // Authentication-first setup has the same continuation as driver-first setup.
    if (
      (!candidateModelId || candidateModelId === DefaultModelId) &&
      availableTabs.some((tab) => tab.value === 'driver')
    ) {
      settingsProducer((draft) => {
        draft.flags.pendingAuthContinuation = false;
      });
      setActiveTab('driver');
    }
  });

  const handleTabChange = useLatestFn((tab: string) => {
    const newTab = tab as IrisConnectionTab;
    // Locked tabs stay focusable to explain themselves, but never activate.
    if (isTabLocked(newTab, authenticationPending)) {
      return;
    }

    settingsProducer((draft) => {
      draft.flags.pendingAuthContinuation = false;
    });

    try {
      setIsLoading(true);

      setActiveTab(newTab);
      // The store mirror is handled by the `[activeTab, settingsProducer]`
      // effect below — `setActiveTab` triggers a render and the effect
      // fires immediately after with the new value.

      // W5 consolidated the advanced-settings open events onto the canonical
      // trackAdvancedSettingsOpened (the prior per-section method was removed).
      // surface/section/subsection remain valid EventProperties.
      // Per rule: stay-in-scope — sole call site fix.
      settingsLifecycleTracker.trackAdvancedSettingsOpened({
        surface: 'iris_connection_tabs',
        section: 'iris-connection',
        subsection: newTab
      });
    } catch (err) {
      // TODO: handle error
    } finally {
      setIsLoading(false);
    }
  });

  // Ensure activeTab is valid when availableTabs change. Authentication is
  // always available and never locked, so a pending camera falls back to it.
  useEffect(() => {
    const isActiveTabAvailable = availableTabs.some((t) => t.value === activeTab);
    if (!isActiveTabAvailable) {
      setActiveTab(
        availableTabs.find((t) => !isTabLocked(t.value, authenticationPending))?.value ??
          'monitoring'
      );
    }
  }, [availableTabs, activeTab, authenticationPending]);

  // When the camera starts waiting for credentials, leave a tab that just
  // locked. Keyed on the verdict alone, so a lagging verdict cannot undo the
  // move to Driver that follows a successful Submit.
  useEffect(() => {
    if (authenticationPending) {
      setActiveTab((tab) => (isTabLocked(tab, authenticationPending) ? 'authentication' : tab));
    }
  }, [authenticationPending]);

  // Mirror the activeTab to the store on every change (including
  // mount and the availableTabs fallback above). The store's
  // `irisConnectionActiveTab` is consumed by the footer global save
  // handler to drive sub-tab-scoped dismissal.
  useEffect(() => {
    settingsProducer((draft) => {
      draft.irisConnectionActiveTab = activeTab;
    });
  }, [activeTab, settingsProducer]);

  const tabContent = (() => {
    switch (activeTab) {
      case 'monitoring':
        return <MonitoringContent agent={agent} />;
      case 'authentication':
        // AuthenticationContent reads `agent.embedded` to decide URX-
        // form-vs-Iris-info-message.
        return <AuthenticationContent agent={agent} onCredentialsSaved={handleCredentialsSaved} />;
      case 'video':
        return <VideoContent />;
      // `<ControlContent>` brings its own two-column layout (settings + preview
      // and PTZ pad), so it takes the plain content path below rather than the
      // monitoring path that supplies a preview column.
      case 'control':
        return <ControlContent />;
      case 'driver':
        return <DriverContent />;
      default:
        return null;
    }
  })();

  const panelContent =
    activeTab === 'monitoring' ? (
      <Columns>
        <Columns.Column>{tabContent}</Columns.Column>
        <Columns.Column>
          <h3>Video preview</h3>
          <Preview />
        </Columns.Column>
      </Columns>
    ) : (
      tabContent
    );

  return (
    <div className={twMerge('flex flex-col gap-24', className)} {...props}>
      {/* Section Header */}
      <div className='flex flex-col gap-16'>
        {/* Secondary Tab Navigation */}
        <Tabs.Root value={activeTab} onChange={handleTabChange}>
          <Tabs.List
            className='mb-28 gap-32 overflow-visible whitespace-nowrap border-0 bg-transparent px-0 text-md'
            role='tablist'
            aria-label={`${vendorName()} Connection Settings`}
          >
            {availableTabs.map((tab) => {
              // Monitoring never gets the icon (it surfaces bridge/agent
              // state, not per-aspect device config). Yellow
              // `text-warning`, not red — matches the tile's
              // `outline-warning` ring and the chip's `bg-warning`
              // accent bar.
              const showWarning =
                tab.value !== 'monitoring' &&
                needsAttentionTabs[tab.value as keyof typeof needsAttentionTabs] === true;
              const locked = isTabLocked(tab.value, authenticationPending);
              const trigger = (
                <Tabs.Trigger
                  key={tab.value}
                  value={tab.value}
                  {...STag(Sel.VideoSettings, 'connection-tab', tab.value)}
                  className={twMerge(
                    SUBCATEGORY_TRIGGER_CLASSNAME,
                    'flex-shrink-0 px-0',
                    locked && 'cursor-not-allowed opacity-50'
                  )}
                  role='tab'
                  aria-selected={activeTab === tab.value}
                  aria-controls={`iris-connection-${tab.value}-panel`}
                  // Per rule: keyboard-reachable-interactive-elements (ai.knowledge/global) —
                  // aria-disabled, not disabled, keeps a locked tab focusable with its reason.
                  aria-disabled={locked || undefined}
                  aria-describedby={locked ? lockReasonId : undefined}
                  // A Tooltip trigger would otherwise replace the tab's own active/inactive state.
                  data-state={activeTab === tab.value ? 'active' : 'inactive'}
                  disabled={isLoading}
                >
                  {showWarning && (
                    // Per `ui-architecture` (ai.knowledge/global):
                    // Tailwind theme tokens only — `text-warning`
                    // honors whitelabel variants (Iris/Hive) without
                    // a hard-coded hex. Icon is `aria-hidden`; the
                    // sr-only span after the label carries the a11y
                    // text so the focus stop count is unchanged.
                    <Icon
                      icon='alert-triangle'
                      aria-hidden='true'
                      className='text-[14px] text-warning'
                    />
                  )}
                  <span className={`${activeTab === tab.value ? 'font-demibold' : 'font-medium'}`}>
                    {tab.label}
                  </span>
                  {showWarning && (
                    // Screen-reader name resolves to "Authentication
                    // needs attention" etc. The visible icon is
                    // `aria-hidden`; this `sr-only` span is the
                    // a11y-only carrier.
                    <span className='sr-only'> needs attention</span>
                  )}
                </Tabs.Trigger>
              );
              return locked ? (
                <Tooltip key={tab.value} zIndex={300} trigger={trigger}>
                  <div className='rounded-4 bg-[white] px-12 py-6 text-sm text-black shadow-lg'>
                    {LOCKED_TAB_REASON}
                    <Arrow className='fill-[white]' />
                  </div>
                </Tooltip>
              ) : (
                trigger
              );
            })}
          </Tabs.List>
        </Tabs.Root>
        {authenticationPending && (
          // Locked tabs' description; a hidden node still supplies aria-describedby text.
          <span id={lockReasonId} hidden>
            {LOCKED_TAB_REASON}
          </span>
        )}
      </div>
      <SectionDescription
        className='mb-[-2.5rem] mt-[-1.7rem]'
        section={TAB_DESCRIPTIONS[activeTab].title}
        description={TAB_DESCRIPTIONS[activeTab].description}
      />
      <HRule className={verification?.status === 'pending' ? 'mb-0' : undefined} />

      {/* Tab Content */}
      <div
        role='tabpanel'
        id={`iris-connection-${activeTab}-panel`}
        aria-labelledby={`iris-connection-${activeTab}-tab`}
        tabIndex={0}
      >
        {verification?.status === 'pending' && (
          <div role='status' aria-live='polite' className='mb-24 flex items-center gap-12 py-12'>
            <Spinner className='h-16 w-16 shrink-0' />
            <span className='leading-130'>
              {verification.step === 'authentication'
                ? 'Verifying credentials…'
                : 'Verifying camera connection…'}
            </span>
          </div>
        )}
        {verification?.step === 'driver' &&
          verification.status !== 'pending' &&
          verification.status !== 'verified' &&
          activeTab === 'driver' && (
            // Working video with unconfirmed control, or no verdict, is yellow; only a rejection
            // or an unvouched failure is red, so a playing camera is never titled as a failure.
            <Callout
              severity={verificationSeverity(verification.status)}
              role='alert'
              title={verificationTitle(verification.status)}
              className='mb-24'
            >
              <Callout.P>{verificationMessage(verification.status)}</Callout.P>
            </Callout>
          )}
        {warning && verification?.status !== 'pending' && (
          <Callout severity='warning' title={warning.title} role='alert' className='mb-24'>
            <Callout.P>
              {activeTab === 'authentication' && isEmbeddedContext && !isTransitory
                ? `${hasSavedCredentials ? 'The camera did not accept its saved credentials.' : 'This camera requires credentials.'} Contact your administrator to update the credentials or re-pair this camera.`
                : warning.message}
            </Callout.P>
          </Callout>
        )}
        {unlistedBrandNote && verification?.status !== 'pending' && (
          <Callout severity='info' title={unlistedBrandNote.title} className='mb-24'>
            <Callout.P>{unlistedBrandNote.message}</Callout.P>
          </Callout>
        )}
        {isLoading ? (
          <div className='flex items-center justify-center py-32'>
            <div className='text-sm text-muted'>Loading...</div>
          </div>
        ) : (
          panelContent
        )}
      </div>
    </div>
  );
};

IrisConnectionSection.displayName = 'IrisConnectionSection';
